import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import Dashboard from './pages/Dashboard';
import Consultores from './pages/Consultores';
import Clientes from './pages/Clientes';
import Sessoes from './pages/Sessoes';
import Pagamentos from './pages/Pagamentos';
import Planos from './pages/Planos';
import RelatorioIA from './pages/RelatorioIA';
import PagamentosConsultores from './pages/PagamentosConsultores';
import CartaSemana from './pages/CartaSemana';
import Banners from './pages/Banners';
import Integracoes from './pages/Integracoes';
import Emails from './pages/Emails';
import Config from './pages/Config';
import Suporte from './pages/Suporte';
import type { PageType, PageMeta } from './types';

const PAGE_META: Record<PageType, PageMeta> = {
  'dashboard': {
    section: 'Dashboard',
    page: 'Visão geral',
    title: 'Dashboard',
    subtitle: 'Visão geral das consultas, faturação e desempenho diário.'
  },
  'consultores': {
    section: 'Operação',
    page: 'Consultores',
    title: 'Consultores',
    subtitle: 'Gestão de consultores humanos (perfil, horários, preços, comissões).'
  },
  'clientes': {
    section: 'Operação',
    page: 'Clientes',
    title: 'Clientes',
    subtitle: 'Consulta rápida de clientes, saldo, sessões e consentimentos RGPD.'
  },
  'sessoes': {
    section: 'Operação',
    page: 'Sessões & Chat',
    title: 'Sessões & Chat',
    subtitle: 'Monitorização das sessões em tempo real e acesso ao histórico de chat.'
  },
  'pagamentos': {
    section: 'Finanças',
    page: 'Pagamentos',
    title: 'Pagamentos',
    subtitle: 'Lista de carregamentos de saldo (IA e humanos), métodos de pagamento e estado.'
  },
  'planos': {
    section: 'Finanças',
    page: 'Planos & Cupões',
    title: 'Planos & Cupões',
    subtitle: 'Configure pacotes de minutos e códigos promocionais.'
  },
  'relatorio-ia': {
    section: 'Finanças',
    page: 'Relatório IA / ChatGPT',
    title: 'Relatório IA / ChatGPT',
    subtitle: 'Faturação e custos das consultas feitas com inteligência artificial.'
  },
  'pagamentos-consultores': {
    section: 'Finanças',
    page: 'Pagamentos a consultores',
    title: 'Pagamentos a consultores',
    subtitle: 'Módulo futuro para gerir pagamentos a consultores humanos.'
  },
  'carta-semana': {
    section: 'Conteúdo',
    page: 'Carta da Semana',
    title: 'Carta da Semana / Fim de semana',
    subtitle: 'Defina a carta, o texto e a data de envio para site e ManyChat.'
  },
  'banners': {
    section: 'Conteúdo',
    page: 'Banners & Textos',
    title: 'Banners & Textos do site',
    subtitle: 'Atualize textos chave e banners do site.'
  },
  'integracoes': {
    section: 'Sistema',
    page: 'Integrações & Tracking',
    title: 'Integrações & Tracking',
    subtitle: 'Configure ManyChat, Google Analytics, Google Ads e Meta Pixel.'
  },
  'emails': {
    section: 'Sistema',
    page: 'Templates de Email',
    title: 'Templates de Email',
    subtitle: 'Mensagens automáticas de boas-vindas, pagamento confirmado e saldo a terminar.'
  },
  'config': {
    section: 'Sistema',
    page: 'Configurações',
    title: 'Configurações gerais',
    subtitle: 'Parâmetros do sistema, IVA, limites de sessão e comportamento do chat IA.'
  },
  'suporte': {
    section: 'Sistema',
    page: 'Suporte & Logs',
    title: 'Suporte & Logs',
    subtitle: 'Registo de atividades importantes e pedidos internos de suporte.'
  }
};

interface BackofficeLayoutProps {
  activePage: PageType;
  children: React.ReactNode;
}

function BackofficeLayout({ activePage, children }: BackofficeLayoutProps) {
  const currentMeta = PAGE_META[activePage];
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close sidebar when route changes (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  return (
    <div className="flex w-full min-h-screen bg-[radial-gradient(circle_at_top,#1c2135_0,#05060a_55%,#020308_100%)] text-text">
      <Sidebar
        activePage={activePage}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="flex-1 flex flex-col min-w-0">
        <Topbar 
          breadcrumb={{ section: currentMeta.section, page: currentMeta.page }}
          title={currentMeta.title}
          subtitle={currentMeta.subtitle}
          onToggleSidebar={() => setSidebarOpen(prev => !prev)}
        />
        {children}
      </main>
    </div>
  );
}

export default function BackofficeApp() {
  return (
    <Routes>
      <Route path="/" element={<BackofficeLayout activePage="dashboard"><Dashboard /></BackofficeLayout>} />
      <Route path="/consultores" element={<BackofficeLayout activePage="consultores"><Consultores /></BackofficeLayout>} />
      <Route path="/clientes" element={<BackofficeLayout activePage="clientes"><Clientes /></BackofficeLayout>} />
      <Route path="/sessoes" element={<BackofficeLayout activePage="sessoes"><Sessoes /></BackofficeLayout>} />
      <Route path="/pagamentos" element={<BackofficeLayout activePage="pagamentos"><Pagamentos /></BackofficeLayout>} />
      <Route path="/planos" element={<BackofficeLayout activePage="planos"><Planos /></BackofficeLayout>} />
      <Route path="/relatorio-ia" element={<BackofficeLayout activePage="relatorio-ia"><RelatorioIA /></BackofficeLayout>} />
      <Route path="/pagamentos-consultores" element={<BackofficeLayout activePage="pagamentos-consultores"><PagamentosConsultores /></BackofficeLayout>} />
      <Route path="/carta-semana" element={<BackofficeLayout activePage="carta-semana"><CartaSemana /></BackofficeLayout>} />
      <Route path="/banners" element={<BackofficeLayout activePage="banners"><Banners /></BackofficeLayout>} />
      <Route path="/integracoes" element={<BackofficeLayout activePage="integracoes"><Integracoes /></BackofficeLayout>} />
      <Route path="/emails" element={<BackofficeLayout activePage="emails"><Emails /></BackofficeLayout>} />
      <Route path="/config" element={<BackofficeLayout activePage="config"><Config /></BackofficeLayout>} />
      <Route path="/suporte" element={<BackofficeLayout activePage="suporte"><Suporte /></BackofficeLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
